import { AxiosError } from 'axios';
import { API_ERROR_CODES, DESIGN_MODES, IMAGERY_PROVIDER, PREMIUM_IMAGERY_PROVIDERS, ROOF_DESIGN_MODES } from '../../constants';
import { SelectorDiv, StyledSelect, IconPremium } from './styles';
import { errorResponseHandler } from 'store/api/AxiosError';
import { changePanel, fetchRoofData } from 'store/slices/RoofDataSlice/roofDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { useMemo, useRef, useState } from 'react';
import { setEnergyDerate, setEneryDerateToQueryParamState } from 'store/slices/QueryParamsSlice';
import { decideDerateBasedUponMode, isModeDefault } from '../../util';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { useMutation } from 'react-query';
import { switchDesignMode, switchRoofDesignMode } from './api';
import { setThreeDState } from 'store/slices/ToolSlice';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import { RoofData } from 'store/slices/RoofDataSlice/types';
import { components, OptionProps, SingleValueProps } from 'react-select';
// import { ReactComponent as PremiumIcon } from 'assets/svg/premium-icon.svg';
import { appToast } from '../Basic/CustomToast';
import { selectActiveAction, TOOL_ACTION } from 'store/selectors/toolSelector';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';

export type ModeOption = { value: string; label: string, sublabel?: string };

// User-facing labels, decoupled from the raw enum value sent to/received from the API.
// IMPORTANT: keys are the wire/API values (unchanged); only the displayed string differs.
// Logic must always key off the enum value — only rendering should use MODE_LABELS[mode].
export const MODE_LABELS: Record<DesignModes, string> = {
	[DESIGN_MODES.IN_HOUSE_SIMULATION]: 'Default',
	[DESIGN_MODES.DEFAULT]: 'Sunroof',
	[DESIGN_MODES.NEARMAP_NREL_AUTOMATIC]: 'NREL (Assisted Design)',
	[DESIGN_MODES.NEARMAP_NREL_MANUAL]: 'NREL (Manual Design)',
	[DESIGN_MODES.DEFAULT_MANUAL]: 'Sunroof (Blank)',
	[DESIGN_MODES.IN_HOUSE_SIMULATION_MANUAL]: 'Default (Blank)',
};

export const options: ModeOption[] = [
	{ value: DESIGN_MODES.IN_HOUSE_SIMULATION, label: MODE_LABELS[DESIGN_MODES.IN_HOUSE_SIMULATION] },
	{ value: DESIGN_MODES.IN_HOUSE_SIMULATION_MANUAL, label: MODE_LABELS[DESIGN_MODES.IN_HOUSE_SIMULATION_MANUAL] },
	{ value: DESIGN_MODES.DEFAULT, label: MODE_LABELS[DESIGN_MODES.DEFAULT] },
	{ value: DESIGN_MODES.DEFAULT_MANUAL, label: MODE_LABELS[DESIGN_MODES.DEFAULT_MANUAL] },
	{ value: DESIGN_MODES.NEARMAP_NREL_AUTOMATIC, label: MODE_LABELS[DESIGN_MODES.NEARMAP_NREL_AUTOMATIC] },
	{ value: DESIGN_MODES.NEARMAP_NREL_MANUAL, label: MODE_LABELS[DESIGN_MODES.NEARMAP_NREL_MANUAL] },
];

export const RoofOnlyOptions: ModeOption[] = [
	{ value: ROOF_DESIGN_MODES.DEFAULT, label: 'Default'},
	{ value: ROOF_DESIGN_MODES.MANUAL, label: 'Manual' },
];

// soalrequivalet modes — shown as a sublabel on the roof selector to indicate the connected solar mode
export const SolarModesEquivalentLabels: Record<DesignModes, string> = {
	[DESIGN_MODES.IN_HOUSE_SIMULATION]: 'Solar Default',
	[DESIGN_MODES.IN_HOUSE_SIMULATION_MANUAL]: 'Solar Default (Blank)',
	[DESIGN_MODES.DEFAULT]: 'Solar Sunroof Simulation',
	[DESIGN_MODES.DEFAULT_MANUAL]: 'Solar Sunroof (Blank)',
	[DESIGN_MODES.NEARMAP_NREL_AUTOMATIC]: ' Solar NREL Assisted',
	[DESIGN_MODES.NEARMAP_NREL_MANUAL] : 'NREL Manual'
};

type Props = {
	designMode: (keyof typeof DESIGN_MODES),
	roofMode: (keyof typeof ROOF_DESIGN_MODES) 
	designUUID: string,
	panelKey: string,
	energyDerateRate: number,
	stateCode?: string,
	billingLogs?: RoofData['billingLogs'],
	autoSwitchStatus?: RoofData['autoSwitchStatus'],
	modeAndItsCorrespondingImagery: RoofData['modeAndItsCorrespondingImagery'],
	designType?: DesignType
	reqToolType?: ToolType
}

const message = 'This will reset all your progress on the map. Are you sure you want to continue?';
const CustomSingleValue = (props: SingleValueProps<ModeOption> & {designType: DesignType}) => {
	const currentValue = props.data;
	return (
		<components.SingleValue {...props}>
			{`${currentValue.label} ${currentValue.sublabel || ''}`}
			{/* {props.data.value === DESIGN_MODES.NEARMAP_NREL_AUTOMATIC && 
			<PremiumIcon style={{ position: 'absolute',top: '12px', right: '10px'}} width={16} height={16}/>
			} */}
		</components.SingleValue>
	);
};

const CustomOption = (props: OptionProps<ModeOption> & { nrelAssistedModeImageryFetched: boolean }) => {
	const { data, innerRef, innerProps, isFocused, isDisabled, isSelected,
		nrelAssistedModeImageryFetched } = props;

	const optionStyles: React.CSSProperties = {
		backgroundColor: isSelected ? 'var(--selectDropdownActiveColor)' 
			: isFocused ? 'var(--selectDropdownHoverColor)' : 'var(--selectDropdownBgColor)',
		color: isSelected ? 'var(--subHeadingColor)' : 'var(--subHeadingColor)',
		padding: '10px 14px',
		cursor: isDisabled ? 'not-allowed' : 'pointer',
		opacity: isDisabled ? 0.6 : 1,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		// display: 'flex'
	};

	return (
		<div
			ref={innerRef}
			{...innerProps}
			style={optionStyles}
		>

			{`${data.label} ${data.sublabel || ''}`}
			{/* {data.value === DESIGN_MODES.NEARMAP_NREL_AUTOMATIC && <IconPremium
				className='primium-icon'>
				<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 10 10" fill="none">
					<path d="M0.695312 3.84296L4.92245 0.902344L9.1496 3.84296V9.35663H0.695312V3.84296Z" fill="#FEB517"/>
					<path d="M6.62795 8.29579L4.98579 7.25829L3.34896 8.29579L3.78083 6.33294L2.33594 5.02064L4.24468 4.84678L4.98579 
					2.99609L5.72689 4.84117L7.63564 5.01503L6.19075 6.33294L6.62795 8.29579Z" fill="white"/>
				</svg> Premium Imagery{nrelAssistedModeImageryFetched && <svg style={{position:'relative', top: '1px'}} 
					xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 10 7" fill="none">
					<path d="M1.58594 3.23438L3.9723 5.5071L8.74503 0.734375" 
						stroke="#FEB517" strokeWidth="1.36364" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M1.58594 3.23438L3.9723 5.5071L8.74503 0.734375" 
						stroke="#FEB517" strokeWidth="1.36364" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>}</IconPremium>} */}
		</div>
	);
};

export default function DesignModesSelector(props: Props) {
	const { designMode, roofMode, designUUID, panelKey, energyDerateRate, 
		stateCode, autoSwitchStatus, billingLogs, 
		modeAndItsCorrespondingImagery, designType='SOLAR', reqToolType='solar' } = props;

	const dispatch = useDispatch<AppDispatch>();
	const [mode, setMode] = useState<string>(designType === 'SOLAR' ? designMode: roofMode);
	const prevMode = useRef<string>(designType === 'SOLAR' ? designMode: roofMode);
	const modeToSwitchTo = useRef<string>('');

	// const { mutate, isLoading } = useSwitchDesignModesQuery();
	const queriesRunningInBackGround = useQueriesRunningInBackGround();

	const { editModeEnabled, deletedRoofIndexes, drawModeEnabled, roofIndexes, deleteObstructionsMode, newlyCreatedFacets, fullScreenEnabled, enableEditObs,
		hideSatelliteOverlay } =
	useSelector((state: RootState) => state.toolNewPostions.data);
	const { activePanelMode } = 	useSelector((state: RootState) => state.panelSlice.data);	
	const { inputFieldsEdited } = useSelector((state: RootState) => state.manualToolModal.data);
	const allowedModes = useSelector((state: RootState) => state.roleState.data.allowedModes);
	const isRecomputing = useSelector(selectIsRecomputing);
	const startShadingRecalc = useStartShadingRecalc();

	const hasUserMadeModificaiton = !!deletedRoofIndexes?.length || !!Object.keys(newlyCreatedFacets)?.length || !!roofIndexes?.length;
	const disableModeSwitch = editModeEnabled
		&& (hasUserMadeModificaiton || activePanelMode || drawModeEnabled || deleteObstructionsMode || enableEditObs );
	const isModeSwitchDisabled = queriesRunningInBackGround || disableModeSwitch || inputFieldsEdited || isRecomputing || hideSatelliteOverlay;

	const switchDesignModeQuery = useMutation({
		mutationKey: 'switch-design-mode',
		mutationFn: async (userSelectedMode: DESIGN_MODES) => {
			const payload:any = {
				uuid: designUUID,
				mode: userSelectedMode,
			};
			if(panelKey)
				payload['panelKey'] = panelKey;
			return await switchDesignMode(payload);
		},
		onSuccess(shadingStatus, userSelectedMode) {
			if (!isModeDefault(userSelectedMode)) {
				const modeEnergyRate = decideDerateBasedUponMode(userSelectedMode, energyDerateRate, stateCode ?? null);
				dispatch(setEnergyDerate({ energyDerate: modeEnergyRate }));
			} else {
				dispatch(setEneryDerateToQueryParamState());
			}

			dispatch(
				changePanel({
					uuid: designUUID,
					panelKey,
					switchingSource: true,
					designType: designType,
					refetchIrradiance: true
				})
			);
			// The target mode's rasters may predate the current panel (changed in another mode).
			startShadingRecalc(shadingStatus);
		},
		onError(err: AxiosError) {
			// eslint-disable-next-line prefer-const
			let { message, error: errCode } = errorResponseHandler(err);
			if(errCode === API_ERROR_CODES.INVALID_MODE_CHECKOUT) {
				message = 'The mode you are trying to checkout isn\'t available for this address, please select a different mode';
			}
			appToast.error(message);
			if (prevMode.current) setMode(prevMode.current);
			setConfirmationModal(false);
		},
		retry(failureCount, error) {
			const { error: parsedErrorMsg } = errorResponseHandler(error as AxiosError);
			console.log('error', error, parsedErrorMsg);
			return (
				parsedErrorMsg !== API_ERROR_CODES.INVALID_MODE_CHECKOUT &&
          failureCount < 2
			);
		},
	});

	const switchRoofDesignModeQuery = useMutation({
		mutationKey: 'switch-roof-design-mode',
		mutationFn: async (userSelectedMode:ROOF_DESIGN_MODES) => {
			const payload = {
				uuid: designUUID,
				roofMode: userSelectedMode,
				solarMode: designMode as DESIGN_MODES,

			};
			await switchRoofDesignMode(payload);
		},
		onSuccess(_, userSelectedMode) {
			dispatch(fetchRoofData({ uuid: designUUID, designType }));
							
		},
		onError(err: AxiosError) {
			// eslint-disable-next-line prefer-const
			let { message, error: errCode } = errorResponseHandler(err);
			if(errCode === API_ERROR_CODES.INVALID_MODE_CHECKOUT) {
				message = 'The mode you are trying to checkout isn\'t available for this address, please select a different mode';
			}
			appToast.error(message);
			if (prevMode.current) setMode(prevMode.current);
			setConfirmationModal(false);
		},
		retry(failureCount, error) {
			const { error: parsedErrorMsg } = errorResponseHandler(error as AxiosError);
			console.log('error', error, parsedErrorMsg);
			return (
				parsedErrorMsg !== API_ERROR_CODES.INVALID_MODE_CHECKOUT && failureCount < 2
			);
		},
	});

	const [confirmationModal, setConfirmationModal] = useState(false);

	const handleConfirmation = () => {
		setConfirmationModal(false);
		prevMode.current = mode;
		setMode(modeToSwitchTo.current);
		dispatch(setThreeDState({enabled: false}));
		if(designType === 'SOLAR') 
			switchDesignModeQuery.mutateAsync(modeToSwitchTo.current as DESIGN_MODES);
		else 
			switchRoofDesignModeQuery.mutateAsync(modeToSwitchTo.current as ROOF_DESIGN_MODES);
	};

	// case not handled: 
	// deafult switched ot vexcel 
	// now switched to nrel assited for first time(default nearmap) but nearmap not avaibel
	// const modeToSwitchIsNrelAssisted = modeToSwitchTo.current === DESIGN_MODES.NEARMAP_NREL_AUTOMATIC;
	// const nearmapIsBilled = !!billingLogs?.some(l => l.imageryProvider === IMAGERY_PROVIDER.NEARMAP);
	// const userHasSwitchedToNrelAssistedOnce = !!modeAndItsCorrespondingImagery?.NEARMAP_NREL_AUTOMATIC;
	// const autoSwitchedToPremium = !!autoSwitchStatus && PREMIUM_IMAGERY_PROVIDERS.includes(autoSwitchStatus.imageryProvider as IMAGERY_PROVIDER);

	// const shouldEnableBilling =
	// 	modeToSwitchIsNrelAssisted &&
	// 	!!autoSwitchStatus &&
	// 	!autoSwitchedToPremium &&
	// 	//check if nearmap (default) if fetched, for the first tiem when swtiching to this mode
	// 	!userHasSwitchedToNrelAssistedOnce && !nearmapIsBilled;

	const CustomOptionWrapper = (props: OptionProps<typeof options[0]>) => {
		// const nrelAssistedModeImageryFetched = (!autoSwitchStatus || autoSwitchedToPremium || !!userHasSwitchedToNrelAssistedOnce || nearmapIsBilled);
		return <CustomOption {...props} nrelAssistedModeImageryFetched={false} />;

	};

	const CustomSingleValueWrapper = (props: SingleValueProps<ModeOption>) => (
		<CustomSingleValue {...props} designType={designType} />
	);

	const dropdownOptions = useMemo(()=>{
		if(designType === 'SOLAR') return options.filter((mode) => allowedModes.includes(mode.value));
		
		if(reqToolType === 'roof') return RoofOnlyOptions.filter((mode) => allowedModes.includes(mode.value));
		return RoofOnlyOptions.filter((mode) => allowedModes.includes(mode.value)).map((o)=> {
			if(o.value === 'Default' && panelKey) {
				o.sublabel = `(Connected to ${SolarModesEquivalentLabels[designMode]})`;
			}
			return o;
		});
	},[allowedModes, designMode, designType, panelKey, reqToolType]);

	const currentModeValue = useMemo(()=>{
		return dropdownOptions.find(option => {
			// if(designType === 'ROOF'){
			// 	if(mode === DESIGN_MODES.NEARMAP_NREL_MANUAL) return option.value === 'Manual';
			// 	return option.value === 'Default';
			// }
			return option.value === mode;
		});
	},[dropdownOptions, mode]);

	return (
		<SelectorDiv className={`${fullScreenEnabled ? 'fullScreenSelector' : 'defaultScreenSelector'}`}>
			<label className='mode-label'>
				Mode
			</label>
			<StyledSelect
				// isLoading={isLoading}
				isDisabled={isModeSwitchDisabled}
				options={dropdownOptions}
				// menuIsOpen={true}
				className="custom-react-select-mode"
				classNamePrefix="cus-select"
				onChange={(data: any) => {
					if (mode === data.value) return;
					modeToSwitchTo.current = data.value;
					setConfirmationModal(true);
				}}
				value={currentModeValue}
				isSearchable={false}
				components={{ SingleValue: CustomSingleValueWrapper , Option: CustomOptionWrapper}}
			/>

			<ConfirmationModal
				open={confirmationModal}
				onCancel={() => setConfirmationModal(false)}
				onConfirm={handleConfirmation}
				message={message}
				// note={shouldEnableBilling? 
				// 	'This mode uses premium imagery which will be charged. Click \'Yes\' to confirm.': ''}
			/>
		</SelectorDiv>

	);
}
